import { useEffect, useState } from 'react';
import { commissionsApi } from '@/lib/api';
import { Commission } from '@/types';
import { format } from 'date-fns';
import clsx from 'clsx';

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

const statusStyles: Record<string, string> = {
  pending: 'badge-warning',
  approved: 'badge-info',
  paid: 'badge-success',
  rejected: 'badge-danger',
  refunded: 'badge-neutral',
};

export default function AffiliateCommissions() {
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchCommissions = async () => {
    setIsLoading(true);
    try {
      const params: { status?: string; page: number; limit: number } = {
        page,
        limit: 20,
      };
      if (filter !== 'all') {
        params.status = filter;
      }

      const response = await commissionsApi.listMine(params);
      if (response.data?.success) {
        setCommissions(response.data?.data?.commissions ?? []);
        setTotalPages(response.data?.data?.pagination?.totalPages ?? 1);
      }
    } catch (error) {
      console.error('Failed to fetch commissions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCommissions();
  }, [filter, page]);

  // Calculate totals (with defensive checks)
  const safeCommissions = commissions || [];
  const totals = {
    pending: safeCommissions.filter(c => c.status === 'pending').reduce((s, c) => s + c.commissionAmount, 0),
    approved: safeCommissions.filter(c => c.status === 'approved').reduce((s, c) => s + c.commissionAmount, 0),
    paid: safeCommissions.filter(c => c.status === 'paid').reduce((s, c) => s + c.commissionAmount, 0),
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-display font-bold text-charcoal">Commissions</h2>
        <p className="text-charcoal/60 mt-1">Track your earned commissions</p>
      </div>

      {/* Summary Cards with accent bars */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl bg-warm-white shadow-sm p-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-tan" />
          <div className="pl-2">
            <p className="text-sm text-charcoal/60 mb-1">Pending</p>
            <p className="font-serif text-3xl text-charcoal leading-none">{formatCurrency(totals.pending)}</p>
            <p className="text-xs text-charcoal/50 mt-1">Awaiting approval</p>
          </div>
        </div>
        <div className="rounded-xl bg-warm-white shadow-sm p-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-rust" />
          <div className="pl-2">
            <p className="text-sm text-charcoal/60 mb-1">Approved</p>
            <p className="font-serif text-3xl text-charcoal leading-none">{formatCurrency(totals.approved)}</p>
            <p className="text-xs text-charcoal/50 mt-1">Ready for payout</p>
          </div>
        </div>
        <div className="rounded-xl bg-warm-white shadow-sm p-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-sage" />
          <div className="pl-2">
            <p className="text-sm text-charcoal/60 mb-1">Paid</p>
            <p className="font-serif text-3xl text-charcoal leading-none">{formatCurrency(totals.paid)}</p>
            <p className="text-xs text-charcoal/50 mt-1">Total received</p>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {['all', 'pending', 'approved', 'paid', 'rejected'].map((status) => (
          <button
            key={status}
            onClick={() => { setFilter(status); setPage(1); }}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              filter === status
                ? 'bg-rust text-white'
                : 'bg-warm-white text-charcoal/70 hover:bg-cream'
            )}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-xl bg-warm-white shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-rust"></div>
          </div>
        ) : safeCommissions.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-charcoal/60">No commissions found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream/50 border-b border-tan/30">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Order ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Order Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Commission</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Payable Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-tan/20">
                {safeCommissions.map((commission) => (
                  <tr key={commission.commissionId} className="hover:bg-cream/30 transition-colors">
                    <td className="px-6 py-4 text-charcoal">{format(new Date(commission.createdAt), 'MMM d, yyyy')}</td>
                    <td className="px-6 py-4">
                      <code className="text-sm text-charcoal/70">{commission.orderId}</code>
                    </td>
                    <td className="px-6 py-4 text-charcoal">{formatCurrency(commission.orderAmount)}</td>
                    <td className="px-6 py-4 font-serif font-medium text-charcoal">
                      {formatCurrency(commission.commissionAmount)}
                      <span className="text-charcoal/50 text-xs ml-1">
                        ({commission.commissionRate}%)
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={clsx('badge', statusStyles[commission.status])}>
                        {commission.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-charcoal/60">
                      {commission.payableAt ? format(new Date(commission.payableAt), 'MMM d, yyyy') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn btn-secondary"
          >
            Previous
          </button>
          <span className="flex items-center px-4 text-charcoal/60">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="btn btn-secondary"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
